import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import BoxCard from "shared/components/box/BoxCard.js";
import BoxMessage from "shared/components/box/BoxMessage.js";
import ItemRowClub from "shared/components/items/ItemRowClub.js";
import ItemRowCompetition from "shared/components/items/ItemRowCompetition.js";
import ItemRowPlayerAssist from "shared/components/items/ItemRowPlayerAssist.js";
import ItemRowUser from "shared/components/items/ItemRowUser.js";
import LoadingSquare from "shared/components/loading/LoadingSquare";
import { getClubs, getCompetitions, getPlayers, getUsers } from "shared/services/api-assistant.js";

interface PageSearchProps {
  yScrollPosition: number;
}

const PageSearch: React.FC<PageSearchProps> = ({ yScrollPosition }) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [fieldValue, setFieldValue] = useState("");
  const [searchValue, setSearchValue] = useState(null);

  const [players, setPlayers] = useState(null);
  const [clubs, setClubs] = useState(null);
  const [users, setUsers] = useState(null);
  const [competitions, setCompetitions] = useState(null);

  const [playerPage, setPlayerPage] = useState(0);
  const [clubPage, setClubPage] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [competitionPage, setCompetitionPage] = useState(0);

  const [canLoadMorePlayers, setCanLoadMorePlayers] = useState(true);
  const [canLoadMoreClubs, setCanLoadMoreClubs] = useState(true);
  const [canLoadMoreUsers, setCanLoadMoreUsers] = useState(true);
  const [canLoadMoreCompetitions, setCanLoadMoreCompetitions] = useState(true);

  const [isLoading, setIsLoading] = useState(false);

  const search = async () => {
    if (searchValue && searchValue.length > 0) {
      navigate({ search: "?q=" + searchValue });

      flushSync(() => {
        setIsLoading(true);

        setClubs(null);
        setPlayers(null);
        setUsers(null);
        setCompetitions(null);

        setPlayerPage(0);
        setClubPage(0);
        setUserPage(0);
        setCompetitionPage(0);

        setCanLoadMorePlayers(true);
        setCanLoadMoreClubs(true);
        setCanLoadMoreUsers(true);
        setCanLoadMoreCompetitions(true);
      });

      try {
        await Promise.all([fetchClubs(), fetchPlayers(), fetchUsers(), fetchCompetitions()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchClubs = () => {
    return new Promise((resolve, reject) => {
      getClubs({
        handleSuccess: (d) => {
          if (!clubs) {
            setClubs(d.data.getClubs);
          } else {
            setClubs(clubs.concat(d.data.getClubs));
          }

          if (d.data.getClubs.length < 10) setCanLoadMoreClubs(false);

          setClubPage(clubPage + 1);
          resolve();
        },
        handleError: (e) => {
          console.error(e);
          reject(e);
        },
        params: {
          limit: 10,
          skip: clubPage * 10,
          ...(/^\d+$/.test(searchValue.trim()) ? { id: Number(searchValue.trim()) } : { search: searchValue.trim() }),
        },
      });
    });
  };

  const fetchPlayers = () => {
    return new Promise((resolve, reject) => {
      getPlayers({
        handleSuccess: (d) => {
          if (!players) {
            setPlayers(d.data.getPlayers);
          } else {
            setPlayers(players.concat(d.data.getPlayers));
          }

          if (d.data.getPlayers.length < 10) setCanLoadMorePlayers(false);

          setPlayerPage(playerPage + 1);
          resolve();
        },
        handleError: (e) => {
          console.error(e);
          reject(e);
        },
        params: {
          limit: 10,
          skip: playerPage * 10,
          ...(/^\d+$/.test(searchValue.trim()) ? { id: Number(searchValue.trim()) } : { search: searchValue.trim() }),
        },
      });
    });
  };

  const fetchUsers = () => {
    return new Promise((resolve, reject) => {
      getUsers({
        handleSuccess: (d) => {
          if (!users) {
            setUsers(d.data.getUsers);
          } else {
            setUsers(users.concat(d.data.getUsers));
          }

          if (d.data.getUsers.length < 10) setCanLoadMoreUsers(false);

          setUserPage(userPage + 1);
          resolve();
        },
        handleError: (e) => {
          console.error(e);
          reject(e);
        },
        params: { search: searchValue, skip: userPage * 10 },
      });
    });
  };

  const fetchCompetitions = () => {
    return new Promise((resolve, reject) => {
      getCompetitions({
        handleSuccess: (d) => {
          if (!competitions) {
            setCompetitions(d.data.getCompetitions);
          } else {
            setCompetitions(competitions.concat(d.data.getCompetitions));
          }

          if (d.data.getCompetitions.length < 30) setCanLoadMoreCompetitions(false);

          setCompetitionPage(competitionPage + 1);
          resolve();
        },
        handleError: (e) => {
          console.error(e);
          reject(e);
        },
        params: {
          limit: 30,
          skip: competitionPage * 30,
          search: searchValue.trim(),
        },
      });
    });
  };

  useEffect(() => {
    if (searchParams.get("q")) {
      setFieldValue(searchParams.get("q"));
      setSearchValue(searchParams.get("q"));
    }
  }, []);

  useEffect(() => {
    if (searchValue && searchValue.length > 0) {
      setClubs(null);
      setPlayers(null);
      setUsers(null);
      setCompetitions(null);
      setIsLoading(true);
      setPlayerPage(0);
      setClubPage(0);
      setUserPage(0);
      setCompetitionPage(0);
      setCanLoadMorePlayers(true);
      setCanLoadMoreClubs(true);
      setCanLoadMoreUsers(true);
      setCanLoadMoreCompetitions(true);
    }
  }, [searchValue]);

  useEffect(() => {
    if (
      searchValue &&
      searchValue.length > 0 &&
      playerPage === 0 &&
      clubPage === 0 &&
      competitionPage === 0 &&
      clubs === null &&
      players === null &&
      competitions === null
    ) {
      search();
    }
  }, [searchValue, playerPage, players, clubPage, clubs, competitionPage, competitions]);

  return (
    <div id="PageSearch" className="d-flex flex-column w-100 h-100">
      <div className="d-flex flex-shrink-0">
        <nav className="MenuTop navbar justify-content-center justify-content-md-start w-100 py-2 px-3">
          <div className="d-flex w-100">
            <div className="d-flex flex-grow-1">
              <input
                type="text"
                className="form-control w-100 me-2"
                value={fieldValue}
                onChange={(v) => setFieldValue(v.target.value)}
                placeholder={"Search players, clubs, users..."}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && fieldValue.length >= 0) {
                    setSearchValue(fieldValue);
                  }
                }}
              />
            </div>

            <div className="d-flex flex-grow-0">
              <button
                type="text"
                className="btn btn-info w-100"
                onClick={() => setSearchValue(fieldValue)}
                disabled={fieldValue.length < 1}
              >
                <i className="bi bi-search text-white"></i>
              </button>
            </div>
          </div>
        </nav>
      </div>

      <div className="d-flex flex-grow-1 overflow-auto  pt-4 pb-4">
        <div className="container max-width-md px-1 px-md-4">
          {(isLoading || (users && users.length > 0)) && (
            <BoxCard
              className="mb-3"
              title="Managers"
              content={
                !users || users.length === 0 ? (
                  <div className="d-flex ratio ratio-21x9">
                    <LoadingSquare />
                  </div>
                ) : (
                  <div class="w-100">
                    {users.map((c) => (
                      <ItemRowUser c={c} />
                    ))}
                    {canLoadMoreUsers && (
                      <button
                        className="btn btn-sm btn-link align-self-start"
                        onClick={() => {
                          fetchUsers();
                        }}
                      >
                        Load more
                      </button>
                    )}{" "}
                  </div>
                )
              }
            />
          )}
          {(isLoading || (clubs && clubs.length > 0)) && (
            <BoxCard
              className="mb-3"
              title="Clubs"
              content={
                !clubs || clubs.length === 0 ? (
                  <div className="d-flex ratio ratio-21x9">
                    <LoadingSquare />
                  </div>
                ) : (
                  <div class="w-100">
                    {clubs.map((c) => (
                      <ItemRowClub c={c} />
                    ))}
                    {canLoadMoreClubs && (
                      <button
                        className="btn btn-sm btn-link align-self-start"
                        onClick={() => {
                          fetchClubs();
                        }}
                      >
                        Load more
                      </button>
                    )}{" "}
                  </div>
                )
              }
            />
          )}
          {(isLoading || (players && players.length > 0)) && (
            <BoxCard
              className="mb-3"
              title="Players"
              content={
                !players || players.length === 0 ? (
                  <div className="d-flex ratio ratio-21x9">
                    <LoadingSquare />
                  </div>
                ) : (
                  <div class="w-100">
                    {players.map((c) => (
                      <ItemRowPlayerAssist p={c} />
                    ))}
                    {canLoadMorePlayers && (
                      <button
                        className="btn btn-sm btn-link align-self-start"
                        onClick={() => {
                          fetchPlayers();
                        }}
                      >
                        Load more
                      </button>
                    )}{" "}
                  </div>
                )
              }
            />
          )}
          {(isLoading || (competitions && competitions.length > 0)) && (
            <BoxCard
              className="mb-3"
              title="Competitions"
              content={
                !competitions || competitions.length === 0 ? (
                  <div className="d-flex ratio ratio-21x9">
                    <LoadingSquare />
                  </div>
                ) : (
                  <div class="w-100">
                    {competitions.map((c) => (
                      <ItemRowCompetition c={c} />
                    ))}
                    {canLoadMoreCompetitions && (
                      <button
                        className="btn btn-sm btn-link align-self-start"
                        onClick={() => {
                          fetchCompetitions();
                        }}
                      >
                        Load more
                      </button>
                    )}{" "}
                  </div>
                )
              }
            />
          )}
          {!isLoading &&
            clubs &&
            clubs.length === 0 &&
            players &&
            players.length === 0 &&
            competitions &&
            competitions.length === 0 &&
            users &&
            users.length === 0 && (
              <BoxCard contentClassName="py-5" content={<BoxMessage content={"No result found"} />} />
            )}
          <div className="pb-4"></div>
        </div>
      </div>
    </div>
  );
};

export default PageSearch;
